"use client";

import { useState } from "react";
import Header from "../Components/Header";
import Sidebar from "../Components/Sidebar";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { Toaster } from "sonner";

const theme = createTheme({
  palette: {
    primary: {
      main: "#FFB300", // Amber-500
    },
    background: {
      default: "#181714",
      paper: "#27272a",
    },
    text: {
      primary: "#ffffff",
      secondary: "#a1a1aa",
    },
  },
  components: {
    MuiTextField: {
      styleOverrides: {
        root: {
          fontSize: "1rem",
          fontWeight: "normal",
          "& .MuiInputLabel-root": {
            color: "white",
            fontWeight: "bold",
            "&.Mui-focused": {
              color: "white",
            },
          },
          "& .MuiOutlinedInput-root": {
            borderRadius: "18px",
            "& fieldset": { borderColor: "white" },
            "&:hover fieldset": { borderColor: "white" },
            "&.Mui-focused fieldset": { borderColor: "white" },
          },
          "& .MuiInputBase-input": {
            color: "white",
          },
          "& .MuiInputLabel-asterisk": {
            color: "#FF4B4B",
            fontSize: "1rem",
          },
          "& .MuiInputAdornment-root .MuiSvgIcon-root": {
            color: "white",
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": {
            color: "white",
            fontWeight: "bold",
            borderRadius: "18px",
            "& fieldset": {
              borderColor: "white",
              paddingLeft: "16px",
            },
            "&:hover fieldset": {
              borderColor: "white",
            },
            "&.Mui-focused fieldset": {
              borderColor: "white",
            },
            "&.Mui-disabled": {
              opacity: 0.7,
              color: "white",
              "-webkit-text-fill-color": "white",
              backgroundColor: "#27272a",
            },
          },
          "& .MuiSelect-icon": {
            color: "white",
            opacity: 0.7, // <<< optional: match icon too
          },
        },
      },
    },

    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: "18px",
          "& .MuiInputBase-input": {
            color: "white",
          },
          "& fieldset": {
            borderColor: "white !important",
            paddingLeft: "10px",
          },
          "&:hover fieldset": {
            borderColor: "white !important",
          },
          "&.Mui-focused fieldset": {
            borderColor: "white !important",
          },
          "& .MuiOutlinedInput-notchedOutline": {
            borderWidth: "2px",
          },
          "&.Mui-disabled": {
            opacity: 0.7,
            backgroundColor: "#27272a",
            color: "white",
            "-webkit-text-fill-color": "white",
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: "white",
          fontWeight: "semiBold",
          "&.Mui-focused": {
            color: "white",
          },
          "& .MuiInputLabel-asterisk": {
            color: "#FF4B4B",
            fontSize: "1rem",
          },
        },
        "&.Mui-disabled": {
          color: "white",
          opacity: 0.7,
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          fontSize: "1rem",
          color: "white",
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          color: "white",
          "&.Mui-checked": {
            color: "#FFB300",
          },
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          backgroundColor: "#27272a",
          color: "white",
          "&:hover": {
            backgroundColor: "#3f3f46",
          },
          "&.Mui-selected": {
            backgroundColor: "#52525b",
            "&:hover": {
              backgroundColor: "#71717a",
            },
          },
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          backgroundColor: "#27272a",
          border: "1px solid #52525b",
        },
      },
    },
    MuiPickersPopper: {
      styleOverrides: {
        paper: {
          backgroundColor: "#27272a",
          color: "#ffffff",
        },
      },
    },
    MuiPickersDay: {
      styleOverrides: {
        root: {
          width: "36px",
          height: "36px",
          fontSize: "0.875rem",
          color: "#ffffff",
          backgroundColor: "transparent",
          borderRadius: "50%",
          "&:hover": {
            backgroundColor: "#3f3f46",
          },
          "&.Mui-selected": {
            backgroundColor: "#FFB300",
            color: "#27272a",
            "&:hover": {
              backgroundColor: "#FFB300",
            },
          },
          "&.MuiPickersDay-today": {
            borderColor: "#FFB300",
          },
        },
      },
    },
    MuiPickersCalendarHeader: {
      styleOverrides: {
        root: {
          color: "#ffffff",
          padding: "16px 16px 8px 16px",
        },
        label: {
          color: "#ffffff",
          fontSize: "1rem",
          fontWeight: 500,
        },
        switchViewButton: {
          color: "#ffffff",
        },
        iconButton: {
          color: "#ffffff",
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          "& .MuiButton-text": {
            color: "#FFB300",
          },
        },
      },
    },
    MuiPickersLayout: {
      styleOverrides: {
        root: {
          backgroundColor: "#27272a",
          color: "#ffffff",
        },
      },
    },
    MuiDayCalendar: {
      styleOverrides: {
        weekDayLabel: {
          color: "#a1a1aa",
          fontSize: "0.75rem",
          width: "36px",
        },
      },
    },
  },
});

export default function AuthenticatedLayout({ children, loader = true }) {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  return (
    <ThemeProvider theme={theme}>
      <Toaster />
      <div className="min-h-screen flex flex-col bg-[#181714]">
        <Header toggleSidebar={toggleSidebar} />
        <div className="flex flex-grow overflow-hidden">
          <Sidebar isOpen={isSidebarOpen} toggleSidebar={toggleSidebar} />
          <main className="flex-grow animate-fade pl-0 sm:pl-20 lg:pl-[5rem] pt-[.8rem] pr-1 sm:pr-2 overflow-auto">
            {children}
          </main>
        </div>
      </div>
    </ThemeProvider>
  );
}
